import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Commonservice } from '../../../services/commonservice';
import {
  buildPaginationItems,
  buildPaginationLabel,
  buildQueryString,
  buildTimestampFileName,
  canShowPagination,
  downloadBlob,
  extractPagination,
  getApiErrorMessage,
} from '../../../helper/table-utils';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ManageEntityFormValue, ManageEntityItem, ManageEntityPagination } from './manage-entity.interfaces';
import { createManageEntityFormValue } from './manage-entity.data';

type ManageEntityModalMode = 'create' | 'edit' | 'view';
type ManageEntitySortField = 'entity_name' | 'entity_type';
type ManageEntitySortOrder = 'asc' | 'desc';

const ENTITY_ALL_FILTER_VALUE = 'all';
const ENTITY_NO_RECORDS_LABEL = 'No records';
const ENTITY_SHOWING_RESULTS_LABEL = 'Showing';
const ENTITY_RESULTS_LABEL = 'results';
const ENTITY_ONLY_XLSX_MESSAGE = 'Only .xlsx files are allowed.';
const ENTITY_FAILED_LOAD_MESSAGE = 'Failed to load entities:';
const ENTITY_FAILED_EXPORT_MESSAGE = 'Failed to export entity data.';
const ENTITY_FAILED_TEMPLATE_MESSAGE = 'Failed to download template.';
type ManageEntityQueryValue = string | number | null | undefined;
type ManageEntityQueryParams = Record<string, ManageEntityQueryValue>;

const ENTITY_TYPE_OPTIONS = ['Government', 'Private', 'Public', 'Joint Venture'];

function developerNameValidator(control: AbstractControl): ValidationErrors | null {
  const name = typeof control.value === 'string' ? control.value.trim() : '';
  if (!name) {
    return { required: true };
  }

  return /[A-Za-z]/.test(name) ? null : { alphabeticCharacterRequired: true };
}

@Component({
  selector: 'app-manage-entity',
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-entity.html',
  styleUrl: './manage-entity.scss',
})
export class ManageEntity implements OnInit{

  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly entityTypeOptions = ENTITY_TYPE_OPTIONS;

  protected readonly filterForm: UntypedFormGroup;
  protected readonly entityForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isDownloadingTemplate = false;
  protected isImporting = false;
  protected isEntityActionMenuOpen = false;
  protected entityModalOpen = false;
  protected deleteModalOpen = false;
  protected modalMode: ManageEntityModalMode = 'create';
  protected selectedEntity: ManageEntityItem | null = null;
  protected formSubmitted = false;
  protected sortBy: ManageEntitySortField | null = null;
  protected sortOrder: ManageEntitySortOrder = 'asc';

  protected entities: ManageEntityItem[] = [];
  protected filteredEntities: ManageEntityItem[] = [];
  protected pagination: ManageEntityPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      entity_type: [ENTITY_ALL_FILTER_VALUE],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.entityForm = this.fb.group({
      entity_name: ['', [developerNameValidator, Validators.maxLength(255)]],
      entity_type: ['', [Validators.required, Validators.maxLength(25)]],
      status: ['', [Validators.maxLength(100)]],
    });
  }

  ngOnInit(): void {
    this.loadEntities(1);

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadEntities(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        entity_type: ENTITY_ALL_FILTER_VALUE,
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.isEntityActionMenuOpen = false;
    this.loadEntities(1);
  }

  protected loadEntities(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      entity_type: this.filterForm.get('entity_type')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_ENTITIES_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.entities = this.mapEntityList(res?.value?.entities ?? res?.entities ?? []);
          this.filteredEntities = [...this.entities];
          this.pagination = this.extractPagination(res?.value) ?? this.pagination;
        },
        error: (err) => {
          console.error(ENTITY_FAILED_LOAD_MESSAGE, err);
          this.entities = [];
          this.filteredEntities = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  protected handleExportEntities(): void {
    if (this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isEntityActionMenuOpen = false;

    this.isExporting = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      entity_type: this.filterForm.get('entity_type')?.value,
      page_no: this.pagination.page_no,
      page_size: this.pagination.page_size,
    });

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_ENTITIES, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildEntityFileName('entities')),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || ENTITY_FAILED_EXPORT_MESSAGE),
      });
  }

  protected handleDownloadTemplate(): void {
    if (this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isEntityActionMenuOpen = false;

    this.isDownloadingTemplate = true;

    this.httpSvc
      .downloadfile(AuthEndPoints.ENTITY_TEMPLATE, ApiMethod.GET)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDownloadingTemplate = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'entity_import_template.xlsx'),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || ENTITY_FAILED_TEMPLATE_MESSAGE),
      });
  }

  protected triggerEntityImportFilePicker(fileInput: HTMLInputElement | null): void {
    if (!fileInput || this.isImporting || this.isExporting || this.isDownloadingTemplate) {
      return;
    }

    this.isEntityActionMenuOpen = false;
    fileInput.click();
  }

  protected toggleEntityActionMenu(): void {
    if (this.isLoading || this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isEntityActionMenuOpen = !this.isEntityActionMenuOpen;
  }

  protected onEntityImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;

    if (!file) {
      return;
    }

    const fileName = file.name || '';
    if (!fileName.toLowerCase().endsWith('.xlsx')) {
      this.commonSvc.showErrorToastr(ENTITY_ONLY_XLSX_MESSAGE);
      if (input) {
        input.value = '';
      }
      return;
    }

    this.isImporting = true;

    const formData = new FormData();
    formData.append('file', file);

    this.httpSvc
      .requestCall(AuthEndPoints.IMPORT_ENTITIES, ApiMethod.POST, formData, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isImporting = false;
          if (input) {
            input.value = '';
          }
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.loadEntities(1);
        }
      });
  }

  protected openAddEntityModal(): void {
    this.modalMode = 'create';
    this.selectedEntity = null;
    this.entityForm.enable({ emitEvent: false });
    this.entityForm.reset(createManageEntityFormValue());
    this.formSubmitted = false;
    this.entityModalOpen = true;
  }

  protected openEditEntityModal(entity: ManageEntityItem): void {
    this.modalMode = 'edit';
    this.selectedEntity = entity;
    this.entityForm.enable({ emitEvent: false });
    this.entityForm.reset({
      entity_name: entity.entity_name,
      entity_type: entity.entity_type,
      status: entity.status ?? '',
    });
    this.formSubmitted = false;
    this.entityModalOpen = true;
  }

  protected openViewEntityModal(entity: ManageEntityItem): void {
    this.modalMode = 'view';
    this.selectedEntity = entity;
    this.entityForm.enable({ emitEvent: false });
    this.entityForm.reset({
      entity_name: entity.entity_name,
      entity_type: entity.entity_type,
      status: entity.status ?? '',
    });
    this.formSubmitted = false;
    this.entityModalOpen = true;
    this.entityForm.disable({ emitEvent: false });
  }

  protected openDeleteEntityModal(entity: ManageEntityItem): void {
    this.selectedEntity = entity;
    this.deleteModalOpen = true;
  }

  protected closeEntityModal(): void {
    this.entityModalOpen = false;
    this.selectedEntity = null;
    this.modalMode = 'create';
    this.entityForm.enable({ emitEvent: false });
    this.entityForm.reset(createManageEntityFormValue());
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedEntity = null;
  }

  protected saveEntity(): void {
    if (this.modalMode === 'view') {
      return;
    }

    this.trimDeveloperName();
    if (this.entityForm.invalid) {
      this.formSubmitted = true;
      this.entityForm.markAllAsTouched();
      return;
    }

    const payload = this.buildEntityPayload();
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_ENTITY : AuthEndPoints.EDIT_ENTITY;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeEntityModal();
          this.loadEntities();
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedEntity) {
      return;
    }

    const payload = { id: this.selectedEntity.id };

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_ENTITY, ApiMethod.DELETE, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadEntities();
        },
      });
  }

  protected trackByEntityId(_index: number, item: ManageEntityItem): number {
    return item.id;
  }

  protected toggleSort(field: ManageEntitySortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadEntities(1);
  }

  protected isSortAscending(field: ManageEntitySortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageEntitySortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.entityForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected isDeveloperNameAlphabeticInvalid(): boolean {
    const control = this.entityForm.get('entity_name');
    return Boolean(control?.hasError('alphabeticCharacterRequired') && (control.touched || this.formSubmitted));
  }

  protected trimDeveloperName(): void {
    const control = this.entityForm.get('entity_name');
    if (typeof control?.value === 'string') {
      control.setValue(control.value.trim());
    }
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected canShowPagination(): boolean {
    return canShowPagination(this.pagination.total_records);
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(this.pagination, ENTITY_NO_RECORDS_LABEL, ENTITY_SHOWING_RESULTS_LABEL, ENTITY_RESULTS_LABEL);
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected goToPreviousPage(): void { this.goToPage(this.pagination.page_no - 1); }
  protected goToNextPage(): void { this.goToPage(this.pagination.page_no + 1); }
  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) return;
    this.loadEntities(pageNo);
  }

  private buildQueryString(params: ManageEntityQueryParams): string {
    return buildQueryString(params, [ENTITY_ALL_FILTER_VALUE]);
  }

  private buildEntityFileName(prefix: string): string {
    return buildTimestampFileName(prefix);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadBlob(blob, fileName);
  }

  private extractPagination(value: unknown): ManageEntityPagination | null {
    return extractPagination<ManageEntityPagination>(value);
  }

  private mapEntityList(items: unknown): ManageEntityItem[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item) => {
      const record = item as Record<string, unknown>;
      return {
        id: Number(record['id'] ?? 0),
        serial_no: Number(record['serial_no'] ?? 0),
        entity_name: typeof record['entity_name'] === 'string' ? record['entity_name'] : 'Untitled entity',
        entity_type: typeof record['entity_type'] === 'string' ? record['entity_type'] : '',
        status: typeof record['status'] === 'string' ? record['status'] : null,
      };
    });
  }

  private buildEntityPayload(): Partial<ManageEntityFormValue> & { id?: number } {
    const formValue: ManageEntityFormValue = this.entityForm.getRawValue();

    const payload: Partial<ManageEntityFormValue> & { id?: number } = {
      entity_name: formValue.entity_name,
      entity_type: formValue.entity_type,
      status: formValue.status,
    };

    if (this.modalMode === 'edit' && this.selectedEntity) {
      payload.id = this.selectedEntity.id;
    }

    return payload;
  }

  private getApiErrorMessage(error: unknown): string | null {
    return getApiErrorMessage(error);
  }
}
